import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { lcTestCompletionAdminApi } from '@/api/lcTestCompletion'

interface Props { organizationId: number }

const STATUS_STYLE: Record<string, string> = {
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  RUNNING: 'bg-cyan-100 text-cyan-800',
  QUEUED: 'bg-blue-100 text-blue-800',
  FAILED: 'bg-red-100 text-red-800',
  INTERRUPTED: 'bg-amber-100 text-amber-800',
  PAUSED: 'bg-amber-100 text-amber-800',
}

export function TestCompletionRunHistory({ organizationId }: Props) {
  const history = useQuery({
    queryKey: ['admin', 'organizations', organizationId, 'test-completion-runs'],
    queryFn: () => lcTestCompletionAdminApi.history(organizationId),
  })
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-5 py-4">
        <h3 className="font-semibold text-slate-900">자동완료 실행 이력</h3>
        <p className="mt-1 text-sm text-slate-500">답안과 점수 원문은 표시하지 않습니다.</p>
      </div>
      {history.isLoading ? (
        <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />이력을 불러오는 중…</div>
      ) : !history.data?.length ? (
        <p className="p-6 text-sm text-slate-500">실행 이력이 없습니다.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">원생 / 범위</th><th className="px-4 py-3">실행자</th><th className="px-4 py-3">처리</th><th className="px-4 py-3">상태</th><th className="px-4 py-3">초기화</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {history.data.map((run) => (
                <tr key={run.id} className="align-top">
                  <td className="px-4 py-3"><div className="font-medium text-slate-900">{run.learnerName}</div><div className="text-xs text-slate-500">{run.endpointTitle}</div></td>
                  <td className="px-4 py-3 text-slate-700">{run.operatorName}<div className="text-xs text-slate-400">{new Date(run.createdAt).toLocaleString()}</div></td>
                  <td className="px-4 py-3 text-slate-700">
                    {run.successCount}/{run.totalCount}
                    {run.failedCount > 0 && (
                      <div className="text-xs text-red-600">
                        실패 {run.failedCount} · {run.failureCode}
                        {typeof run.failureDetails?.message === 'string' && (
                          <div className="mt-0.5 max-w-sm break-words text-red-500">{run.failureDetails.message}</div>
                        )}
                        {typeof run.failureDetails?.componentId === 'string' && (
                          <div className="text-red-500">component: {run.failureDetails.componentId}</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${STATUS_STYLE[run.status] ?? 'bg-slate-100 text-slate-700'}`}>{run.status}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-600">{run.reset ? <><div className="font-medium text-slate-800">{run.reset.actorName}</div><div>{run.reset.reason}</div><div className="text-slate-400">{new Date(run.reset.completedAt).toLocaleString()}</div></> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
